import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { getInvestorAuctionerUrl } from "../../utils/config";
import { ApiRequestParams, handleFetchApi } from "../../utils/api";

// Initial state for the account slice
interface InitialState {
  myBidData: any
  myBidUpdates: any[], // Track local changes to the Today array, initialized as an empty array
  loading: boolean,
  error: any,
  removedAuctionsIds: number[]
};
const initialState: InitialState = {
  myBidData: {
    data: {
      Today: [], // Initialize Today as an empty array
    },
  },
  myBidUpdates: [], // Track local changes to the Today array, initialized as an empty array
  loading: false,
  error: null,
  removedAuctionsIds: []
};

export const MyBidApi = createAsyncThunk<any, string | number>(
  "MyBid",
  async (payload, thunkApi) => {
    try {

      const params: ApiRequestParams = {
        baseUrl: getInvestorAuctionerUrl(),
        endpoint: `auction/proxyauction/${payload}`,

      }
      const response = await handleFetchApi(params);
      return response.data; // Return API response
    } catch (error: any) {
      return thunkApi.rejectWithValue(error.response?.data);
    }
  }
);

const mybidSlice = createSlice({
  name: "MyBid",
  initialState,
  reducers: {
    addBidToToday: (state, action) => {
      const newBid = action.payload;
      if (!newBid?.id) return; // Ignore invalid bids
      // Check if the bid already exists in the myBidUpdates array by comparing the id
      const isDuplicate = state.myBidUpdates.some((update: any) => update.id === newBid.id);

      if (!isDuplicate) {
        // If no duplicate, add the new bid to the array using concat (non-mutating)
        state.myBidUpdates = [...state.myBidUpdates, newBid]; // Use spread operator for immutability
      } else {
        console.log("Duplicate bid detected, not adding:", newBid);
      }
    },
    removeExcludedId: (state, action) => {
      const idToRemove = action.payload;
      // Filter out the item to remove using filter (non-mutating)
      const updatedExcludedIds = state.removedAuctionsIds.filter((id: number) => id !== idToRemove);
      console.log(updatedExcludedIds, "Excluded ids ")
      if (updatedExcludedIds.length === state.removedAuctionsIds.length) {
        console.log("Item not found in myBidUpdates to remove");
      } else {
        state.removedAuctionsIds = updatedExcludedIds;
      }
    },
    clearMyBidUpdates: (state, action) => {
      const idToRemove = action.payload;
      if (!idToRemove) {
        console.error("No ID provided for removal");
        return;
      }
      if (!state.removedAuctionsIds.includes(idToRemove)) {
        state.removedAuctionsIds.push(idToRemove);
      }
      // Filter out the item to remove using filter (non-mutating)
      const updatedBidUpdates = state.myBidUpdates.filter((update: any) => update.id !== idToRemove);
      console.log(updatedBidUpdates, "prop in slice ")
      if (updatedBidUpdates.length === state.myBidUpdates.length) {
        console.log("Item not found in myBidUpdates to remove");
      } else {
        state.myBidUpdates = updatedBidUpdates;
      }

    },
    clearAllMyBidUpdates: (state) => {
      state.myBidUpdates = [];
      state.removedAuctionsIds = [];
    },
  },
  extraReducers: (builder) => {

    builder
      .addCase(MyBidApi.pending, (state) => {

        state.loading = true;
        // state.myBidData = { data: { Today: [] } };  // Set a fresh empty array
      })
      .addCase(MyBidApi.fulfilled, (state, action) => {
        state.loading = false;

        // Check if the API returned an empty data array or a message about no auctions
        if (action.payload?.data?.length === 0) {
          // If no auctions, we ensure Today is empty
          state.myBidData = {
            data: {
              Today: [], // Ensure Today is empty when no auctions are available
            },
          };
          console.log("No auctions available.");
        } else {
          // If data is present, we need to combine myBidUpdates with the API response
          const newMyBidData = { ...action.payload }; // Clone the API response to avoid mutating state

          // Ensure Today exists and is initialized as an empty array if it's missing
          const todayData = newMyBidData?.data?.Today || [];

          // If there are updates in myBidUpdates, we need to combine them with API data
          if (state.myBidUpdates.length > 0) {

            // 1. Get the current time
            const startOfToday = new Date();
            startOfToday.setHours(0, 0, 0, 0); // 11:59:59.999 PM today
            // 2. Filter out expired entries from myBidUpdates
            state.myBidUpdates = state.myBidUpdates.filter((update: any) => {
              const saleTime = new Date(update.srpSaleDateTime || update.openingDate);
              console.log(`Sale Time: ${saleTime} | Start of Today: ${startOfToday}`);
              return saleTime >= startOfToday; // keep only auctions from today and future
            });
            // Combine myBidUpdates with todayData, making sure there are no duplicates based on 'id'
            const updatedToday = [
              ...todayData,
              ...state.myBidUpdates.filter(
                (update: any) => !todayData.some((auction: any) => auction.id === update.id) // Avoid duplicates by checking 'id'
              ),
            ];

            // Set the combined data into Today
            newMyBidData.data.Today = updatedToday;

          } else {
            // If there are no local updates, just use the API's Today data
            newMyBidData.data.Today = todayData;
          }

          // Update the state with the merged data
          state.myBidData = newMyBidData;
        }
      })


      .addCase(MyBidApi.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Failed to link account";
      });
  },
});

export const { addBidToToday, clearMyBidUpdates, removeExcludedId, clearAllMyBidUpdates } = mybidSlice.actions;
export default mybidSlice.reducer;
